// @react-native-google-signin/google-signin for the web, using Google Identity
// Services. signIn() opens Google's popup, then reads the profile so the
// response has the same { data: { user: { id, name, email, ... } } } shape
// the native module returns. The backend only needs id, name and email.
//
// The web client ID must list the site's origin under "Authorised JavaScript
// origins" in Google Cloud Console (e.g. http://localhost:8091). Google only
// accepts localhost or real domains there, not LAN IPs like 192.168.x.x.
const GIS_SRC = 'https://accounts.google.com/gsi/client';
const SCOPES = 'openid email profile';

export const statusCodes = {
  SIGN_IN_CANCELLED: 'SIGN_IN_CANCELLED',
  IN_PROGRESS: 'IN_PROGRESS',
  PLAY_SERVICES_NOT_AVAILABLE: 'PLAY_SERVICES_NOT_AVAILABLE',
  SIGN_IN_REQUIRED: 'SIGN_IN_REQUIRED',
};

let clientId = null;
let gisPromise = null;
let accessToken = null;
let currentUser = null;
let inProgress = false;

// Load the script as early as configure() so signIn() can open the popup
// while the browser still counts the button tap as a user gesture.
const loadGis = () => {
  if (!gisPromise) {
    gisPromise = new Promise((resolve, reject) => {
      if (window.google?.accounts?.oauth2) {
        return resolve(window.google);
      }
      const script = document.createElement('script');
      script.src = GIS_SRC;
      script.async = true;
      script.onload = () => resolve(window.google);
      script.onerror = () => {
        gisPromise = null;
        reject(new Error('Could not load Google Sign-In'));
      };
      document.head.appendChild(script);
    });
  }
  return gisPromise;
};

const codedError = (code, message) => Object.assign(new Error(message), {code});

const requestToken = google =>
  new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPES,
      callback: response =>
        response.error
          ? reject(
              codedError(
                response.error,
                response.error_description || response.error,
              ),
            )
          : resolve(response.access_token),
      error_callback: err =>
        reject(
          err?.type === 'popup_closed'
            ? codedError(statusCodes.SIGN_IN_CANCELLED, 'Sign in cancelled')
            : codedError(
                err?.type || 'UNKNOWN',
                err?.message || 'Google sign-in failed',
              ),
        ),
    });
    client.requestAccessToken();
  });

export const GoogleSignin = {
  configure({webClientId} = {}) {
    clientId = webClientId;
    loadGis().catch(() => {});
  },

  hasPlayServices: () => Promise.resolve(true),

  async signIn() {
    if (!clientId) {
      throw new Error('GoogleSignin.configure({ webClientId }) was not called');
    }
    if (inProgress) {
      throw codedError(statusCodes.IN_PROGRESS, 'Sign in in progress');
    }
    inProgress = true;
    try {
      accessToken = await requestToken(await loadGis());
      const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {Authorization: `Bearer ${accessToken}`},
      });
      if (!res.ok) {
        throw new Error('Could not read the Google profile');
      }
      const profile = await res.json();
      currentUser = {
        idToken: null,
        serverAuthCode: null,
        scopes: SCOPES.split(' '),
        user: {
          id: profile.sub,
          name: profile.name ?? null,
          email: profile.email,
          photo: profile.picture ?? null,
          givenName: profile.given_name ?? null,
          familyName: profile.family_name ?? null,
        },
      };
      return {type: 'success', data: currentUser};
    } finally {
      inProgress = false;
    }
  },

  isSignedIn: () => Promise.resolve(Boolean(currentUser)),
  hasPreviousSignIn: () => Boolean(currentUser),
  getCurrentUser: () => currentUser,
  signInSilently: () =>
    currentUser
      ? Promise.resolve({type: 'success', data: currentUser})
      : Promise.resolve({type: 'noSavedCredentialFound', data: null}),
  getTokens: () => Promise.resolve({idToken: null, accessToken}),

  async revokeAccess() {
    if (accessToken) {
      window.google?.accounts?.oauth2?.revoke(accessToken);
    }
    accessToken = null;
    currentUser = null;
  },

  async signOut() {
    accessToken = null;
    currentUser = null;
  },
};

export const GoogleSigninButton = () => null;
export const isErrorWithCode = error => Boolean(error && error.code);
